import { forwardRef, useId } from 'react'
import clsx from 'clsx'

interface Props extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> {
  etiqueta?: string
  ayuda?: string
  error?: string
  /** Ícono a la izquierda, dentro del campo. */
  icono?: React.ReactNode
  /** Contenido a la derecha: un botón de mostrar contraseña, una unidad. */
  sufijo?: React.ReactNode
  claseContenedor?: string

  /**
   * Texto en mono tabular.
   *
   * Es un booleano y no un className abierto a propósito: la tipografía de los
   * números es una CONVENCIÓN del sistema (la clase .num), no una decisión de
   * cada pantalla. Con className suelto, en algún formulario iba a aparecer un
   * precio en la tipografía del texto y la columna dejaría de alinearse.
   */
  mono?: boolean
}

// Input con etiqueta, ayuda y error.
//
// El error va SIEMPRE debajo del campo y no en un cartel global: un formulario
// que dice "hay errores" arriba obliga a buscar cuál, y con seis campos eso es
// una adivinanza.
export const Campo = forwardRef<HTMLInputElement, Props>(function Campo(
  { etiqueta, ayuda, error, icono, sufijo, claseContenedor, mono, id, ...resto },
  ref,
) {
  const generado = useId()
  const idCampo = id ?? generado
  const idAyuda = `${idCampo}-ayuda`
  const idError = `${idCampo}-error`

  return (
    <div className={claseContenedor}>
      {etiqueta && (
        <label htmlFor={idCampo} className="etiqueta">
          {etiqueta}
        </label>
      )}

      <div className="relative">
        {icono && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-tinta-tenue">
            {icono}
          </span>
        )}

        <input
          ref={ref}
          id={idCampo}
          className={clsx(
            'campo',
            mono && 'num',
            error && 'campo-error',
            icono && 'pl-10',
            sufijo && 'pr-12',
          )}
          // Que el lector de pantalla anuncie la ayuda y el error, no solo el
          // borde rojo.
          aria-invalid={!!error}
          aria-describedby={clsx(ayuda && idAyuda, error && idError) || undefined}
          {...resto}
        />

        {sufijo && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2">{sufijo}</span>
        )}
      </div>

      {error ? (
        <p id={idError} className="error" role="alert">
          {error}
        </p>
      ) : (
        ayuda && (
          <p id={idAyuda} className="ayuda">
            {ayuda}
          </p>
        )
      )}
    </div>
  )
})
